import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, Environment, OrbitControls, PresentationControls, ContactShadows, Float, Html } from '@react-three/drei';
import { Vector3, Group, Mesh, MeshStandardMaterial } from 'three';
import { motion } from 'framer-motion';

// Base Product Viewer Props
export interface BaseProductViewerProps {
  modelPath: string;
  modelScale?: number;
  autoRotate?: boolean;
  rotationSpeed?: number;
  enableZoom?: boolean;
  enablePan?: boolean;
  height?: string | number;
  width?: string | number;
  backgroundColor?: string;
  environmentPreset?: 'apartment' | 'city' | 'dawn' | 'forest' | 'lobby' | 'night' | 'park' | 'studio' | 'sunset' | 'warehouse';
  spotlightPosition?: [number, number, number];
  spotlightIntensity?: number;
  ambientLightIntensity?: number;
  showControls?: boolean;
  infoOverlay?: React.ReactNode;
  initialCameraPosition?: [number, number, number];
  onModelClick?: (e: any) => void;
  isSelected?: boolean;
  className?: string;
  interactive?: boolean;
  modelRotation?: [number, number, number];
  contactShadow?: boolean;
  float?: boolean;
  floatSpeed?: number;
  onError?: () => void;
}

// 3D model with material effects
const ProductModel = ({
  modelPath,
  modelScale = 1,
  autoRotate = false,
  rotationSpeed = 0.005,
  spotlightIntensity = 1,
  onModelClick,
  isSelected = false,
  modelRotation = [0, 0, 0],
}: Pick<BaseProductViewerProps, 'modelPath' | 'modelScale' | 'autoRotate' | 'rotationSpeed' | 'spotlightIntensity' | 'onModelClick' | 'isSelected' | 'modelRotation'>) => {
  const modelRef = useRef<Group>();
  let gltf;

  // Enhanced model loading with specific logging
  try {
    gltf = useGLTF(modelPath);
    console.log(`Successfully loaded GLTF model from: ${modelPath}`);
  } catch (error: any) {
    // Log the specific error and trigger the fallback
    console.error(`Error loading GLTF model from ${modelPath}:`, error.message || error);

    // Notify parent components about the error
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('model-load-error', { detail: { modelPath, error } }));
    }

    // Return null to prevent rendering attempt
    return null;
  }

  // Auto-rotation animation
  useFrame(() => {
    if (autoRotate && modelRef.current) {
      modelRef.current.rotation.y += rotationSpeed;
    }
  });
  
  // Highlight effect when selected
  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.traverse((child) => {
        if (child instanceof Mesh && child.material instanceof MeshStandardMaterial) {
          child.material.emissive.set(isSelected ? 0x333333 : 0x000000);
        }
      });
    }
  }, [isSelected]);
  
  return (
    <group 
      ref={modelRef} 
      onClick={onModelClick}
      rotation={[modelRotation[0], modelRotation[1], modelRotation[2]]}
      scale={[modelScale, modelScale, modelScale]}
    >
      <primitive object={gltf.scene} />
    </group>
  );
};

// Main component
export const BaseProductViewer: React.FC<BaseProductViewerProps> = ({
  modelPath,
  modelScale = 1,
  autoRotate = false,
  rotationSpeed = 0.005,
  enableZoom = true,
  enablePan = false,
  height = 400,
  width = '100%',
  backgroundColor = 'transparent',
  environmentPreset = 'studio',
  spotlightPosition = [10, 10, 10],
  spotlightIntensity = 0.8,
  ambientLightIntensity = 0.5,
  showControls = true,
  infoOverlay,
  initialCameraPosition = [0, 0, 5],
  onModelClick,
  isSelected = false,
  className = '',
  interactive = true,
  modelRotation = [0, 0, 0],
  contactShadow = true,
  float = false,
  floatSpeed = 1.5,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [environmentError, setEnvironmentError] = useState(false);

  // Handle error state
  useEffect(() => {
    // Check if the model exists by trying to fetch it
    const checkModelExists = async () => {
      try {
        const response = await fetch(modelPath, { method: 'HEAD' });
        if (!response.ok) {
          // Model not found via HEAD request
          setHasError(true);
          setIsLoading(false);
          console.error(`Model not found at ${modelPath} (HEAD request failed with status ${response.status})`);
          if (onError) {
            onError();
          }
          window.dispatchEvent(new CustomEvent('model-load-error', { detail: { modelPath, error: `HTTP status ${response.status}` } }));
        } else {
          // HEAD request successful, proceed with loading
          console.log(`Model found via HEAD request: ${modelPath}`);
          // Introduce a small delay before setting loading to false
          // to allow the model component to attempt loading
          setTimeout(() => {
            // Only set loading false if no error has been flagged by ProductModel's try/catch
            if (!hasError) {
              setIsLoading(false);
            }
          }, 200); // Delay slightly
        }
      } catch (error: any) {
        // Network or other fetch error during HEAD request
        setHasError(true);
        setIsLoading(false);
        console.error(`Error checking model existence for ${modelPath}:`, error.message || error);
        if (onError) {
          onError();
        }
        window.dispatchEvent(new CustomEvent('model-load-error', { detail: { modelPath, error } }));
      }
    };

    checkModelExists();

    // Listen for the error event dispatched by ProductModel
    const handleModelLoadError = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.modelPath === modelPath) {
        console.log(`Received model-load-error event for ${modelPath}`);
        setHasError(true);
        setIsLoading(false);
        if (onError) {
          onError();
        }
      }
    };

    window.addEventListener('model-load-error', handleModelLoadError);

    return () => {
      window.removeEventListener('model-load-error', handleModelLoadError);
    };
  }, [modelPath, onError]); // Removed hasError dependency to avoid loop

  // Handle environment loading errors
  useEffect(() => {
    const handleHdrError = (e: ErrorEvent) => {
      if (e.message && e.message.includes('.hdr')) {
        console.warn('HDR environment map failed to load, using fallback lighting');
        setEnvironmentError(true);
      }
    };
    
    // Listen for error events
    window.addEventListener('error', handleHdrError);
    
    return () => {
      window.removeEventListener('error', handleHdrError);
    };
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <div 
        style={{ 
          height: typeof height === 'number' ? `${height}px` : height, 
          width: typeof width === 'number' ? `${width}px` : width,
          backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
        className={`base-product-viewer ${className}`}
      >
        <div className="loading-spinner">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2">Loading 3D Model...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (hasError) {
    return (
      <div 
        style={{ 
          height: typeof height === 'number' ? `${height}px` : height, 
          width: typeof width === 'number' ? `${width}px` : width,
          backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
        className={`base-product-viewer error ${className}`}
      >
        <div className="error-message text-red-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-2 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>Failed to load 3D model</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ 
        height: typeof height === 'number' ? `${height}px` : height, 
        width: typeof width === 'number' ? `${width}px` : width,
        backgroundColor,
        position: 'relative'
      }}
      className={`base-product-viewer ${className}`}
    >
      <Canvas
        camera={{ position: new Vector3(...initialCameraPosition), fov: 45 }}
        style={{ width: '100%', height: '100%' }}
      >
        {/* Environment lighting with fallback */}
        {!environmentError && (
          <React.Suspense fallback={null}>
            <Environment 
              preset={environmentPreset} 
              files={undefined}
              background={false}
            />
          </React.Suspense>
        )}
        {/* Fallback lighting that's always available */}
        <ambientLight intensity={environmentError ? 1.0 : ambientLightIntensity} />
        <directionalLight 
          position={[5, 5, 5]} 
          intensity={environmentError ? 2.0 : 1.5} 
          castShadow 
        />
        <spotLight 
          position={spotlightPosition} 
          intensity={spotlightIntensity} 
          penumbra={1} 
          castShadow 
        />

        {interactive ? (
          /* Interactive controls with presentation wrapper */
          <PresentationControls
            global
            rotation={[0, 0, 0]}
            polar={[-Math.PI / 4, Math.PI / 4]}
            azimuth={[-Math.PI / 4, Math.PI / 4]}
            // Using simpler controls due to type compatibility issues
            speed={1.5}
            zoom={1.2}
          >
            {float ? (
              <Float 
                speed={floatSpeed} 
                rotationIntensity={0.5} 
                floatIntensity={0.5}
              >
                <ProductModel
                  modelPath={modelPath}
                  modelScale={modelScale}
                  autoRotate={autoRotate}
                  rotationSpeed={rotationSpeed}
                  spotlightIntensity={spotlightIntensity}
                  onModelClick={onModelClick}
                  isSelected={isSelected}
                  modelRotation={modelRotation}
                />
              </Float>
            ) : (
              <ProductModel
                modelPath={modelPath}
                modelScale={modelScale}
                autoRotate={autoRotate}
                rotationSpeed={rotationSpeed}
                spotlightIntensity={spotlightIntensity}
                onModelClick={onModelClick}
                isSelected={isSelected}
                modelRotation={modelRotation}
              />
            )}
          </PresentationControls>
        ) : (
          /* Static display */
          <ProductModel
            modelPath={modelPath}
            modelScale={modelScale}
            autoRotate={autoRotate}
            rotationSpeed={rotationSpeed}
            spotlightIntensity={spotlightIntensity}
            onModelClick={onModelClick}
            isSelected={isSelected}
            modelRotation={modelRotation}
          />
        )}

        {/* Optional controls for manual camera manipulation */}
        {showControls && (
          <OrbitControls 
            enableZoom={enableZoom} 
            enablePan={enablePan}
            autoRotate={autoRotate}
            autoRotateSpeed={1}
          />
        )}

        {/* Contact shadow for realistic ground effect */}
        {contactShadow && (
          <ContactShadows 
            position={[0, -1.5, 0]} 
            opacity={0.6} 
            scale={10} 
            blur={2.5} 
            far={5} 
          />
        )}

        {/* Info overlay */}
        {infoOverlay && (
          <Html position={[0, 1.5, 0]} center>
            <div className="overlay bg-white/80 dark:bg-black/80 p-2 rounded-md text-sm">
              {infoOverlay}
            </div>
          </Html>
        )}
      </Canvas>
    </motion.div>
  );
};

// Error boundary for the entire component
const ProductViewerWithErrorHandling: React.FC<BaseProductViewerProps> = (props) => {
  return (
    <React.Suspense
      fallback={
        <div 
          style={{ 
            height: typeof props.height === 'number' ? `${props.height}px` : props.height, 
            width: typeof props.width === 'number' ? `${props.width}px` : props.width,
            backgroundColor: props.backgroundColor || 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} 
          className={`base-product-viewer ${props.className || ''}`}
        >
          <div className="suspense-fallback">
            <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-2">Loading Component...</span>
          </div>
        </div>
      }
    >
      <BaseProductViewer {...props} />
    </React.Suspense>
  );
};

export default ProductViewerWithErrorHandling; 